import { useState, useEffect } from 'react';
import { useCurrentCalendarColor } from '../contexts/CurrentCalendarContext';
import type { RecurrencePattern } from '../lib/rrule';

const DAY_NAMES = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
];
const POSITION_NAMES = ['1st', '2nd', '3rd', '4th'];

interface Props {
  value: RecurrencePattern;
  onChange: (pattern: RecurrencePattern) => void;
  disabled?: boolean;
  eventDate?: Date;
}

export default function RecurrencePicker({
  value,
  onChange,
  disabled = false,
  eventDate,
}: Props) {
  const accentColor = useCurrentCalendarColor();
  const [pattern, setPattern] =
    useState<RecurrencePattern>(value);

  useEffect(() => {
    setPattern(value);
  }, [value]);

  function updatePattern(newPattern: RecurrencePattern) {
    setPattern(newPattern);
    onChange(newPattern);
  }

  return (
    <div className="space-y-3">
      {/* Pattern type selector */}
      <div>
        <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
          Repeats
        </label>
        <select
          value={pattern.type}
          onChange={e => {
            const type = e.target
              .value as RecurrencePattern['type'];
            switch (type) {
              case 'none':
                updatePattern({ type: 'none' });
                break;
              case 'daily':
                updatePattern({
                  type: 'daily',
                  interval: 1,
                });
                break;
              case 'weekly': {
                // Auto-select the event's day of week when switching to weekly
                const dayOfWeek = eventDate?.getDay() ?? 1; // Default to Monday if no date
                updatePattern({
                  type: 'weekly',
                  interval: 1,
                  days: [dayOfWeek],
                });
                break;
              }
              case 'monthly':
                updatePattern({
                  type: 'monthly',
                  position: 1,
                  day: 1,
                });
                break;
              case 'yearly':
                updatePattern({
                  type: 'yearly',
                  interval: 1,
                });
                break;
            }
          }}
          disabled={disabled}
          className="input-field"
        >
          <option value="none">Does not repeat</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>

      {/* Daily options */}
      {pattern.type === 'daily' && (
        <div className="flex items-center gap-2">
          <label className="font-mono text-xs text-ink-400">
            Every
          </label>
          <input
            type="number"
            min={1}
            max={365}
            value={pattern.interval}
            onChange={e =>
              updatePattern({
                type: 'daily',
                interval: parseInt(e.target.value) || 1,
              })
            }
            disabled={disabled}
            className="input-field w-16 text-center"
          />
          <span className="font-mono text-xs text-ink-400">
            day{pattern.interval !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Weekly options */}
      {pattern.type === 'weekly' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="font-mono text-xs text-ink-400">
              Every
            </label>
            <input
              type="number"
              min={1}
              max={52}
              value={pattern.interval}
              onChange={e =>
                updatePattern({
                  ...pattern,
                  interval: parseInt(e.target.value) || 1,
                })
              }
              disabled={disabled}
              className="input-field w-16 text-center"
            />
            <span className="font-mono text-xs text-ink-400">
              week{pattern.interval !== 1 ? 's' : ''}
            </span>
          </div>
          <div>
            <label className="block font-mono text-xs text-ink-400 mb-1.5">
              On these days
            </label>
            <div className="flex flex-wrap gap-1">
              {DAY_NAMES.map((day, idx) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    const days = pattern.days.includes(idx)
                      ? pattern.days.filter(d => d !== idx)
                      : [...pattern.days, idx].sort(
                          (a, b) => a - b
                        );
                    updatePattern({ ...pattern, days });
                  }}
                  disabled={disabled}
                  className={[
                    'px-2 py-1 rounded-sm text-xs font-mono transition-colors border',
                    pattern.days.includes(idx)
                      ? 'text-white shadow-sm'
                      : 'bg-paper-200 text-ink-600 border-paper-300',
                    !disabled && !pattern.days.includes(idx)
                      ? 'hover:bg-paper-300'
                      : '',
                  ].join(' ')}
                  style={
                    pattern.days.includes(idx) &&
                    accentColor
                      ? {
                          backgroundColor: accentColor,
                          borderColor: accentColor,
                        }
                      : undefined
                  }
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Yearly options */}
      {pattern.type === 'yearly' && (
        <div className="flex items-center gap-2">
          <label className="font-mono text-xs text-ink-400">
            Every
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={pattern.interval}
            onChange={e =>
              updatePattern({
                ...pattern,
                interval: parseInt(e.target.value) || 1,
              })
            }
            disabled={disabled}
            className="input-field w-16 text-center"
          />
          <span className="font-mono text-xs text-ink-400">
            year{pattern.interval !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Monthly options */}
      {pattern.type === 'monthly' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <select
              value={pattern.position}
              onChange={e =>
                updatePattern({
                  ...pattern,
                  position: parseInt(e.target.value) || 1,
                })
              }
              disabled={disabled}
              className="input-field w-20"
            >
              {POSITION_NAMES.map((name, idx) => (
                <option key={name} value={idx + 1}>
                  {name}
                </option>
              ))}
            </select>
            <select
              value={pattern.day}
              onChange={e =>
                updatePattern({
                  ...pattern,
                  day: parseInt(e.target.value) || 1,
                })
              }
              disabled={disabled}
              className="input-field"
            >
              {DAY_NAMES.map((name, idx) => (
                <option key={name} value={idx}>
                  {name}
                </option>
              ))}
            </select>
            <span className="font-mono text-xs text-ink-400">
              of every month
            </span>
          </div>
        </div>
      )}

      {/* End options - shown for all recurring types */}
      {pattern.type !== 'none' && (
        <div className="space-y-2 pt-2 border-t border-paper-200">
          <label className="block font-mono text-xs text-ink-400 uppercase tracking-widest">
            Ends
          </label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="recurrence-end"
                checked={!pattern.until}
                onChange={() => {
                  const { until: _, ...rest } = pattern;
                  updatePattern(rest as RecurrencePattern);
                }}
                disabled={disabled}
                className="accent-[var(--accent-color)]"
                style={{ accentColor }}
              />
              <span className="font-mono text-xs text-ink-600">
                Never
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="recurrence-end"
                checked={!!pattern.until}
                onChange={() => {
                  // Default to 3 months from event date
                  const defaultUntil = eventDate
                    ? new Date(
                        eventDate.getTime() +
                          90 * 24 * 60 * 60 * 1000
                      )
                    : new Date(
                        Date.now() +
                          90 * 24 * 60 * 60 * 1000
                      );
                  updatePattern({
                    ...pattern,
                    until: defaultUntil.toISOString(),
                  });
                }}
                disabled={disabled}
                className="accent-[var(--accent-color)]"
                style={{ accentColor }}
              />
              <span className="font-mono text-xs text-ink-600">
                On date
              </span>
            </label>
            {pattern.until && (
              <input
                type="date"
                value={pattern.until.slice(0, 10)}
                onChange={e => {
                  // Set to end of selected day (23:59:59)
                  const date = new Date(
                    e.target.value + 'T23:59:59'
                  );
                  updatePattern({
                    ...pattern,
                    until: date.toISOString(),
                  });
                }}
                disabled={disabled}
                className="input-field w-40"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
